import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { XeroInvoice } from '../../types/xero';
import { Bill } from '../../types/accounting';
import { parseXeroDate } from '../../utils/xeroDate';
import InvoiceQuickViewModal from '../dashboard/calendar/InvoiceQuickViewModal';
import { BillViewModal } from '../bills/components/BillViewModal';

function xeroToBill(x: XeroInvoice): Bill {
  const toNativeStatus = (s: string) => {
    if (s === 'AUTHORISED') return 'APPROVED' as const;
    if (s === 'PAID') return 'PAID' as const;
    if (s === 'VOIDED' || s === 'DELETED') return 'VOIDED' as const;
    return 'DRAFT' as const;
  };
  const date = parseXeroDate(x.Date || x.DateString);
  const dueDate = parseXeroDate(x.DueDate || x.DueDateString);
  return {
    id: x.InvoiceID,
    billNumber: x.InvoiceNumber || '',
    reference: x.Reference || null,
    status: toNativeStatus(x.Status),
    currencyCode: x.CurrencyCode || 'AUD',
    total: x.Total ?? 0,
    amountDue: x.AmountDue ?? 0,
    amountPaid: x.AmountPaid ?? 0,
    contactId: x.Contact?.ContactID || null,
    contactName: x.Contact?.Name || null,
    date: date ? date.toISOString().slice(0, 10) : '',
    dueDate: dueDate ? dueDate.toISOString().slice(0, 10) : '',
    lineItems: (x.LineItems || []).map(li => ({
      description: li.Description || '',
      quantity: li.Quantity ?? 1,
      unitAmount: li.UnitAmount ?? 0,
      lineAmount: li.LineAmount ?? 0,
      taxAmount: li.TaxAmount,
      accountCode: li.AccountCode,
      itemCode: li.ItemCode,
    })),
    legacy_xero_id: x.InvoiceID,
    createdAt: null,
    updatedAt: null,
  };
}

interface MonthDetailModalProps {
  show: boolean;
  onHide: () => void;
  month: string; // Format: "2024-01"
  monthLabel: string; // Format: "Jan 2024"
  invoices: XeroInvoice[];
  bills: XeroInvoice[];
}

interface PaymentItem {
  id: string;
  invoiceId: string; // Store the original invoice ID to find it later
  number: string;
  displayName: string;
  contactName: string;
  amount: number;
  date: Date;
  reference?: string;
}

export function MonthDetailModal({ show, onHide, month, monthLabel, invoices, bills }: MonthDetailModalProps) {
  // State for viewing individual items
  const [selectedInvoice, setSelectedInvoice] = useState<XeroInvoice | null>(null);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  
  // Process invoices for this month (money in)
  const { moneyInItems, moneyInByContact, totalMoneyIn } = useMemo(() => {
    const items: PaymentItem[] = [];
    const byContact: Record<string, number> = {};
    
    invoices.forEach((invoice) => {
      if (invoice.Status === 'VOIDED') return;
      if (invoice.Payments && invoice.Payments.length > 0) {
        invoice.Payments.forEach(payment => {
          const paymentDate = parseXeroDate(payment.Date);
          if (paymentDate) {
            const paymentMonth = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
            if (paymentMonth === month) {
              const contactName = invoice.Contact?.Name || 'Unknown';
              items.push({
                id: `${invoice.InvoiceID}-${payment.PaymentID}`,
                invoiceId: invoice.InvoiceID,
                number: invoice.InvoiceNumber,
                displayName: invoice.InvoiceNumber,
                contactName,
                amount: payment.Amount,
                date: paymentDate,
                reference: invoice.Reference,
              });
              byContact[contactName] = (byContact[contactName] || 0) + payment.Amount;
            }
          }
        });
      } else if (invoice.AmountPaid > 0) {
        const invoiceDate = parseXeroDate((invoice as any).Date || (invoice as any).DateString);
        if (invoiceDate) {
          const invoiceMonth = `${invoiceDate.getFullYear()}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}`;
          if (invoiceMonth === month) {
            const contactName = invoice.Contact?.Name || 'Unknown';
            items.push({
              id: invoice.InvoiceID,
              invoiceId: invoice.InvoiceID,
              number: invoice.InvoiceNumber,
              displayName: invoice.InvoiceNumber,
              contactName,
              amount: invoice.AmountPaid,
              date: invoiceDate,
              reference: invoice.Reference,
            });
            byContact[contactName] = (byContact[contactName] || 0) + invoice.AmountPaid;
          }
        }
      }
    });

    // Sort by amount descending
    items.sort((a, b) => b.amount - a.amount);
    const total = items.reduce((sum, item) => sum + item.amount, 0);

    return { moneyInItems: items, moneyInByContact: byContact, totalMoneyIn: total };
  }, [invoices, month]);

  // Process bills for this month (money out / expenses)
  const { moneyOutItems, moneyOutByContact, totalMoneyOut } = useMemo(() => {
    const items: PaymentItem[] = [];
    const byContact: Record<string, number> = {};
    
    bills.forEach((bill) => {
      if (bill.Status === 'VOIDED') return;
      const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bill.InvoiceNumber || '');
      const supplierName = bill.Contact?.Name || 'Unknown';
      const displayName = isGuid ? `${supplierName} Expense` : bill.InvoiceNumber;

      if (bill.Payments && bill.Payments.length > 0) {
        bill.Payments.forEach(payment => {
          const paymentDate = parseXeroDate(payment.Date);
          if (paymentDate) {
            const paymentMonth = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
            if (paymentMonth === month) {
              items.push({
                id: `${bill.InvoiceID}-${payment.PaymentID}`,
                invoiceId: bill.InvoiceID,
                number: bill.InvoiceNumber,
                displayName,
                contactName: supplierName,
                amount: payment.Amount,
                date: paymentDate,
                reference: bill.Reference,
              });
              byContact[supplierName] = (byContact[supplierName] || 0) + payment.Amount;
            }
          }
        });
      } else if (bill.AmountPaid > 0) {
        // Fallback to bill date
        const billDate = parseXeroDate((bill as any).Date || (bill as any).DateString);
        if (billDate) {
          const billMonth = `${billDate.getFullYear()}-${String(billDate.getMonth() + 1).padStart(2, '0')}`;
          if (billMonth === month) {
            items.push({
              id: bill.InvoiceID,
              invoiceId: bill.InvoiceID,
              number: bill.InvoiceNumber,
              displayName,
              contactName: supplierName,
              amount: bill.AmountPaid,
              date: billDate,
              reference: bill.Reference,
            });
            byContact[supplierName] = (byContact[supplierName] || 0) + bill.AmountPaid;
          }
        }
      }
    });

    // Sort by amount descending
    items.sort((a, b) => b.amount - a.amount);
    const total = items.reduce((sum, item) => sum + item.amount, 0);

    return { moneyOutItems: items, moneyOutByContact: byContact, totalMoneyOut: total };
  }, [bills, month]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Pie chart options for Money In
  const moneyInChartOptions: ApexOptions = {
    chart: {
      type: 'pie',
      fontFamily: 'inherit',
    },
    labels: Object.keys(moneyInByContact),
    colors: ['#3E97FF', '#50CD89', '#7239EA', '#F6C000', '#009EF7', '#FFC700', '#17C653', '#5014D0'],
    legend: { show: false },
    dataLabels: { enabled: false },
    tooltip: {
      y: { formatter: (val) => formatCurrency(val) },
    },
    stroke: { width: 1, colors: ['#fff'] },
  };

  // Pie chart options for Money Out
  const moneyOutChartOptions: ApexOptions = {
    chart: {
      type: 'pie',
      fontFamily: 'inherit',
    },
    labels: Object.keys(moneyOutByContact),
    colors: ['#F1416C', '#FFA800', '#7239EA', '#50CD89', '#009EF7', '#FFC700', '#17C653', '#5014D0'],
    legend: { show: false },
    dataLabels: { enabled: false },
    tooltip: {
      y: { formatter: (val) => formatCurrency(val) },
    },
    stroke: { width: 1, colors: ['#fff'] },
  };

  const netCashFlow = totalMoneyIn - totalMoneyOut;

  // Find original invoice/bill by ID
  const findInvoice = (invoiceId: string) => invoices.find(inv => inv.InvoiceID === invoiceId) || null;
  const findBill = (billId: string): Bill | null => {
    const x = bills.find(b => b.InvoiceID === billId);
    return x ? xeroToBill(x) : null;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3">
          <i className="bi bi-calendar3"></i>
          {monthLabel}
          <span className={`badge ${netCashFlow >= 0 ? 'badge-light-success' : 'badge-light-danger'}`}>
            {netCashFlow >= 0 ? '+' : ''}{formatCurrency(netCashFlow)} net
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row g-5">
          {/* Money In Card */}
          <div className="col-lg-6">
            <div className="card card-bordered h-100">
              <div className="card-header border-0 pt-5 pb-0">
                <h3 className="card-title align-items-start flex-column">
                  <span className="card-label fw-bold text-primary fs-4">
                    <i className="bi bi-arrow-down-circle-fill text-primary me-2"></i>
                    Money In
                  </span>
                </h3>
              </div>
              <div className="card-body pt-0" style={{ overflow: 'visible' }}>
                {/* Pie Chart */}
                {Object.keys(moneyInByContact).length > 0 ? (
                  <div className="d-flex justify-content-center position-relative" style={{ zIndex: 2, paddingBottom: '25px' }}>
                    <div style={{ filter: 'drop-shadow(0 8px 20px rgba(0, 0, 0, 0.15))' }}>
                      <Chart
                        options={moneyInChartOptions}
                        series={Object.values(moneyInByContact)}
                        type="pie"
                        height={320}
                        width={320}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No payments received this month
                  </div>
                )}

                {/* Invoice List */}
                {moneyInItems.length > 0 && (
                  <div className="table-responsive" style={{ maxHeight: '250px', marginTop: '-10px', position: 'relative', zIndex: 1 }}>
                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2">
                      <thead className="sticky-top" style={{ background: 'var(--kt-modal-bg)' }}>
                        <tr className="text-muted fs-8 text-uppercase">
                          <th>Invoice</th>
                          <th>Customer</th>
                          <th className="text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {moneyInItems.map((item) => (
                          <tr 
                            key={item.id} 
                            className="cursor-pointer"
                            onClick={() => setSelectedInvoice(findInvoice(item.invoiceId))}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <span className="fw-semibold fs-7 text-hover-primary">{item.displayName}</span>
                              {item.reference && (
                                <span className="text-muted d-block fs-8">{item.reference}</span>
                              )}
                            </td>
                            <td className="text-muted fs-7">{item.contactName}</td>
                            <td className="text-end fw-bold text-success fs-7">
                              {formatCurrency(item.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Money Out Card */}
          <div className="col-lg-6">
            <div className="card card-bordered h-100">
              <div className="card-header border-0 pt-5 pb-0">
                <h3 className="card-title align-items-start flex-column">
                  <span className="card-label fw-bold text-danger fs-4">
                    <i className="bi bi-arrow-up-circle-fill text-danger me-2"></i>
                    Expenses
                  </span>
                </h3>
              </div>
              <div className="card-body pt-0" style={{ overflow: 'visible' }}>
                {/* Pie Chart */}
                {Object.keys(moneyOutByContact).length > 0 ? (
                  <div className="d-flex justify-content-center position-relative" style={{ zIndex: 2, paddingBottom: '25px' }}>
                    <div style={{ filter: 'drop-shadow(0 8px 20px rgba(0, 0, 0, 0.15))' }}>
                      <Chart
                        options={moneyOutChartOptions}
                        series={Object.values(moneyOutByContact)}
                        type="pie"
                        height={320}
                        width={320}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No expenses paid this month
                  </div>
                )}

                {/* Bill List */}
                {moneyOutItems.length > 0 && (
                  <div className="table-responsive" style={{ maxHeight: '250px', marginTop: '-10px', position: 'relative', zIndex: 1 }}>
                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2">
                      <thead className="sticky-top" style={{ background: 'var(--kt-modal-bg)' }}>
                        <tr className="text-muted fs-8 text-uppercase">
                          <th>Bill</th>
                          <th>Supplier</th>
                          <th className="text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {moneyOutItems.map((item) => (
                          <tr 
                            key={item.id}
                            className="cursor-pointer"
                            onClick={() => setSelectedBill(findBill(item.invoiceId))}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <span className="fw-semibold fs-7 text-hover-primary">{item.displayName}</span>
                              {item.reference && (
                                <span className="text-muted d-block fs-8">{item.reference}</span>
                              )}
                            </td>
                            <td className="text-muted fs-7">{item.contactName}</td>
                            <td className="text-end fw-bold text-danger fs-7">
                              {formatCurrency(item.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-light" onClick={onHide}>
          Close
        </button>
      </Modal.Footer>

      {/* Invoice Quick View Modal */}
      <InvoiceQuickViewModal
        isOpen={!!selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        invoice={selectedInvoice}
        viewType="invoice_due"
      />

      {/* Bill View Modal */}
      <BillViewModal
        show={!!selectedBill}
        bill={selectedBill}
        onHide={() => setSelectedBill(null)}
      />
    </Modal>
  );
}

export default MonthDetailModal;
